import { useDebouncedValue } from "@mirage/mosaics/BaseProfilePage/RichTextArea/useDebouncedValue";
import { useConversationEvents } from "@mirage/service-fss-bridge/hooks/bridgeObservers";
import {
  ConversationSource,
  FSSFile,
  FSSFolder,
} from "@mirage/service-fss-bridge/service/bridge_types";
import { useMemo, useRef } from "react";

//TODO(DISCOVERY-7403): use stacks when implemented
type FSSChatContextResult = {
  source: ConversationSource | undefined;
  files: FSSFile[];
  folders: FSSFolder[];
  stacks: string[];
  surface: string;
};

export function useFSSChatContext(delay: number = 200): FSSChatContextResult {
  const rawEvent = useConversationEvents();
  const event = useDebouncedValue(rawEvent, delay);
  const processedEvents = useRef<Set<string>>(new Set());

  const cachedResult = useRef<FSSChatContextResult>({
    source: undefined,
    files: [],
    folders: [],
    stacks: [],
    surface: "",
  });

  const result = useMemo(() => {
    // Check if we've already processed this event UUID
    if (!event || processedEvents.current.has(event.uuid)) {
      return cachedResult.current;
    }
    processedEvents.current.add(event.uuid);

    const eventData = {
      file: undefined,
      files: [],
      folders: [],
      stacks: [],
      surface: "",
      ...event?.data,
    };

    const surface = event?.surface ?? "";
    const initialFiles = [...eventData.files];
    const files = eventData.file
      ? [...initialFiles, eventData.file]
      : initialFiles;
    const folders = [...eventData.folders];
    const stacks = eventData.stacks || [];
    const source = event?.source;

    cachedResult.current = { source, files, folders, stacks, surface };
    return cachedResult.current;
  }, [event]);

  return result;
}
